import { useClearRefinements, useToggleRefinement } from 'react-instantsearch';
import { Button } from '@fpsm/cxy-components/Button';
import { RadioGroup, RadioGroupItem } from '@fpsm/cxy-components/RadioGroup';

import { type BettingInsightsResultAttribute, type EsIndex } from '~/configuration/types';

import styles from './styles/FormRefinement.module.scss';

export type FormRefinementProps<TIndex extends EsIndex> = {
  attribute: BettingInsightsResultAttribute<TIndex>;
  isDisabled?: boolean;
  label: string;
};

export function FormRefinement<TIndex extends EsIndex>({
  attribute,
  isDisabled = false,
  label,
}: FormRefinementProps<TIndex>) {
  const attributes = {
    placedLast1: `${attribute}.placed.last1`,
    placedLast3: `${attribute}.placed.last3`,
    placedLast6: `${attribute}.placed.last6`,
  };

  const { canRefine: canRefineClear, refine: clearRefinement } = useClearRefinements({
    includedAttributes: [attributes.placedLast1, attributes.placedLast3, attributes.placedLast6],
  });
  const {
    canRefine: canRefinePlacedLast1,
    refine: refinePlacedLast1,
    value: valuePlacedLast1,
  } = useToggleRefinement({
    attribute: attributes.placedLast1,
  });
  const {
    canRefine: canRefinePlacedLast3,
    refine: refinePlacedLast3,
    value: valuePlacedLast3,
  } = useToggleRefinement({
    attribute: attributes.placedLast3,
  });
  const {
    canRefine: canRefinePlacedLast6,
    refine: refinePlacedLast6,
    value: valuePlacedLast6,
  } = useToggleRefinement({
    attribute: attributes.placedLast6,
  });

  const refinementMap = {
    [attributes.placedLast1]: () => refinePlacedLast1({ isRefined: valuePlacedLast1.isRefined }),
    [attributes.placedLast3]: () => refinePlacedLast3({ isRefined: valuePlacedLast3.isRefined }),
    [attributes.placedLast6]: () => refinePlacedLast6({ isRefined: valuePlacedLast6.isRefined }),
  };

  const handleValueChange = (value: string) => {
    clearRefinement();
    if (refinementMap[value]) refinementMap[value]();
  };

  return (
    <div className={styles.formRefinementContainer}>
      <span className={styles.label}>{label}</span>
      <RadioGroup onValueChange={handleValueChange} value="default">
        <RadioGroupItem
          checked={valuePlacedLast1.isRefined}
          disabled={isDisabled || !canRefinePlacedLast1}
          id={attributes.placedLast1}
          key={attributes.placedLast1}
          value={attributes.placedLast1}
        >
          Last Time Out
        </RadioGroupItem>
        <RadioGroupItem
          checked={valuePlacedLast3.isRefined}
          disabled={isDisabled || !canRefinePlacedLast3}
          id={attributes.placedLast3}
          key={attributes.placedLast3}
          value={attributes.placedLast3}
        >
          Within Last 3
        </RadioGroupItem>
        <RadioGroupItem
          checked={valuePlacedLast6.isRefined}
          disabled={isDisabled || !canRefinePlacedLast6}
          id={attributes.placedLast6}
          key={attributes.placedLast6}
          value={attributes.placedLast6}
        >
          Within Last 6
        </RadioGroupItem>
      </RadioGroup>
      <Button
        className={styles.resetButton}
        disabled={isDisabled || !canRefineClear}
        onClick={clearRefinement}
        variant="ghost"
      >
        Reset
      </Button>
    </div>
  );
}
